import {
	initFilterBar,
	getInlineObj,
	paintChoiceActives,
	updateAllFiltersCount,
	updateClearChip,
	bindPriceControls,
	markScopeDirty,
	toQS,
	cleanParams,
	adjustPopoverPosition,
	resolveModalGoto,
	has3Chars
} from './filtersCore';
import { initShowCardTabs } from './ticketBoothTabs';
import mountShowSearch from './showSearch';
import { createFilterTracker } from './filterAnalytics';

/**
 * Saves the current state of checkbox, radio, and date/price inputs within a
 * popover panel so it can be restored if the user cancels without applying.
 *
 * @param {Element} panel - The popover panel element to snapshot.
 */
function snapshotPanel(panel) {
	if (!panel) {
		return;
	}

	const snap = {
		checks: Array.from(panel.querySelectorAll('input[type="checkbox"], input[type="radio"]'))
			.map((i) => ({ name: i.name, value: i.value, checked: i.checked })),
		fields: ['from', 'to', 'max'].map((n) => {
			const el = panel.querySelector(`[name="${ n }"]`);
			return { name: n, value: el ? el.value : '' };
		})
	};

	panel.__snap = snap;
}

/**
 * Restores checkbox, radio, and date/price inputs within a popover panel to
 * the state captured by the most recent snapshotPanel() call.
 *
 * @param {Element} panel - The popover panel element to restore.
 */
function restorePanel(panel) {
	const s = panel?.__snap;
	if (!s) {
		return;
	}

	s.checks.forEach(({ name, value, checked }) => {
		panel.querySelectorAll(`input[name="${ CSS.escape(name) }"]`).forEach((i) => {
			if (i.type === 'radio') {
				i.checked = (i.value === value) && checked;
			} else if (i.type === 'checkbox' && i.value === value) {
				i.checked = checked;
			}
		});
	});

	s.fields.forEach(({ name, value }) => {
		const el = panel.querySelector(`[name="${ CSS.escape(name) }"]`);
		if (el) {
			el.value = value;
		}
	});
}

/**
 * Initialises the Today's Tickets filter bar, including lazy-load pagination,
 * popover panels, the All Filters modal, and the show search typeahead.
 */
export default function todaysTicketsFilters() {
	const bar = document.querySelector('[data-todays-tickets-filterbar]');
	if (!bar) {
		return;
	}

	const form = bar.closest('form');
	const list = document.querySelector('[data-results]');
	const endpoint = bar.getAttribute('data-endpoint');
	const sentinel = document.querySelector('[data-lazy-load-sentinel]');

	if (!form || !list || !endpoint) {
		return;
	}

	if (!sentinel) {
		console.warn("Today's Tickets: Lazy load sentinel not found.");
	}

	const MULTI = new Set(['time', 'genre', 'shows']);

	const DEFAULTS = {
		q: '', from: '', to: '', today: '', tomorrow: '', access: '', offers: '',
		max: '', time: '', genre: '', shows: [], sort: 'name_asc'
	};

	const CLEAR_DEFAULTS = {
		...DEFAULTS,
		today: '1'
	};

	const SORT_MAP = {
		'name-atz': 'name_asc',
		'name-zta': 'name_desc',
		'date-old': 'date_old',
		'date-new': 'date_new',
		'price-low': 'price_low',
		'price-high': 'price_high'
	};

	const normaliseSort = (v) => SORT_MAP[ v ] || v || 'name_asc';

	const setHeaderChip = (sel, on) => {
		bar.querySelectorAll(sel).forEach((b) => {
			b.classList.toggle('is-active', !!on);
			b.setAttribute('aria-pressed', on ? 'true' : 'false');
		});
	};

	const setTrigger = (key, on) => setHeaderChip(`[data-open="${ key }"]`, on);

	const isDateActive = () => !!(form.querySelector('input[name="from"]')?.value || form.querySelector('input[name="to"]')?.value);
	const isPriceActive = () => {
		// Single-thumb max-only slider; min axis removed across all filter bars.
		const max = form.querySelector('input[name="max"]')?.value ?? '';
		return max !== '' && Number(max) < 100;
	};
	const isTimeActive = () => !!form.querySelector('input[name="time[]"]:checked');
	const isGenreActive = () => !!form.querySelector('input[name="genre[]"]:checked');

	const setAllTriggersFromForm = () => {
		updateDateChipActive();
		setTrigger('price', isPriceActive());
		setTrigger('time', isTimeActive());
		setTrigger('genre', isGenreActive());
	};

	const updateDateChipActive = () => setTrigger('date', isDateActive());

	/*
	 * Today/Tomorrow toggle logic:
	 * - Default state: Today only.
	 * - Clicking Tomorrow when only Today is active: both become active.
	 * - Clicking either chip when both are active: that chip is deactivated.
	 * - Both chips cannot be deactivated simultaneously.
	 */
	const toggleDayChip = (key) => {
		const iToday = form.querySelector('input[name="today"]');
		const iTomorrow = form.querySelector('input[name="tomorrow"]');
		const todayOn = (iToday?.value === '1');
		const tomorrowOn = (iTomorrow?.value === '1');

		const clearDateRange = () => {
			const f = form.querySelector('input[name="from"]');
			const t = form.querySelector('input[name="to"]');
			if (f) {
				f.value = '';
			}
			if (t) {
				t.value = '';
			}
			updateDateChipActive();
		};

		if (key === 'online_today') {
			if (todayOn && tomorrowOn) {
				if (iToday) {
					iToday.value = '';
				}
				setHeaderChip('[data-filter-toggle="online_today"]', false);
			} else if (todayOn && !tomorrowOn) {
				// Prevent deactivating the only active day.
				return;
			} else {
				if (iToday) {
					iToday.value = '1';
				}
				setHeaderChip('[data-filter-toggle="online_today"]', true);
			}
		} else if (key === 'online_tomorrow') {
			if (todayOn && tomorrowOn) {
				if (iTomorrow) {
					iTomorrow.value = '';
				}
				setHeaderChip('[data-filter-toggle="online_tomorrow"]', false);
			} else if (!todayOn && tomorrowOn) {
				// Prevent deactivating the only active day.
				return;
			} else {
				if (iTomorrow) {
					iTomorrow.value = '1';
				}
				setHeaderChip('[data-filter-toggle="online_tomorrow"]', true);
			}
		}

		clearDateRange();
	};

	let showSearch = null;
	const filterTracker = createFilterTracker('todays_tickets');

	/**
	 * Reads the current form state and returns a normalised params object
	 * suitable for passing to the REST endpoint.
	 *
	 * @return {Object} Normalised filter parameters.
	 */
	const paramsFromForm = () => {
		const rawObj = getInlineObj(form, MULTI);
		const out = {};

		['q', 'max', 'from', 'to'].forEach((k) => {
			const v = String(rawObj[ k ] || '').trim();
			if (k === 'max') {
				out[ k ] = v;
			} else if (v !== '') {
				out[ k ] = v;
			}
		});

		['today', 'tomorrow', 'access', 'offers'].forEach((k) => {
			const v = String(rawObj[ k ] || '').trim();
			if (v === '1') {
				out[ k ] = '1';
			}
		});

		['time', 'genre'].forEach((k) => {
			let arr = Array.isArray(rawObj[ k ])
				? rawObj[ k ]
				: (rawObj[ k ] ? String(rawObj[ k ]).split(',') : []);

			arr = arr.map((s) => String(s).trim()).filter(Boolean);
			if (arr.length) {
				out[ k ] = [...new Set(arr)].sort();
			}
		});

		/**
		 * Read show IDs from the show search module's state, not from FormData.
		 * Double optional chaining is used because showSearch may be null
		 * during the first paramsFromForm call (inside initFilterBar), and
		 * getIds may not be present on all builds.
		 */
		const showIds = showSearch?.getIds?.() ?? [];
		if (showIds.length) {
			out.shows = showIds;
		} else {
			delete out.shows;
		}

		const s = normaliseSort(rawObj.sort || 'name_asc');
		if (s !== 'name_asc') {
			out.sort = s;
		}

		return out;
	};

	let isLoadingMore = false;
	let lazyLoadObserver = null;

	/**
	 * Fetches the next page of results and appends the rendered HTML to the
	 * results list, using offset/limit values stored on the list element.
	 */
	const loadMore = async () => {
		if (!sentinel || isLoadingMore) {
			return;
		}

		const total = parseInt(list.dataset.total || '0', 10);
		const offset = parseInt(list.dataset.offset || '0', 10);
		const limit = parseInt(list.dataset.limit || '24', 10);

		if (offset >= total) {
			if (sentinel) {
				sentinel.style.display = 'none';
			}
			if (lazyLoadObserver) {
				lazyLoadObserver.disconnect();
			}
			return;
		}

		isLoadingMore = true;
		if (sentinel) {
			sentinel.classList.add('is-loading');
		}

		/*
		* Snapshot the active filters at the point this request is issued.
		* If the visitor changes the filter bar (for example toggling
		* Today/Tomorrow) while this request is still in flight, the
		* comparison below will no longer match once the response returns.
		*/
		const requestQs = toQS(cleanParams(paramsFromForm(), DEFAULTS));

		try {
			const params = paramsFromForm();
			params.offset = offset;
			params.limit = limit;

			const qs = toQS(cleanParams(params, DEFAULTS));
			const r = await fetch(`${ endpoint }?${ qs }`);

			if (!r.ok) {
				throw new Error('Network response was not OK.');
			}

			const data = await r.json();

			/*
			* Discard the response if the filter bar has moved on since this
			* request was made. Appending it now would attach show cards for
			* the previous filter selection underneath the correct, current
			* list, and would overwrite list.dataset with stale pagination
			* values.
			*/
			if (toQS(cleanParams(paramsFromForm(), DEFAULTS)) !== requestQs) {
				return;
			}

			list.insertAdjacentHTML('beforeend', data.html || '');
			initShowCardTabs();

			const newOffset = (data.offset || 0) + (data.limit || 24);
			list.dataset.total = data.total || total;
			list.dataset.offset = newOffset;

			if (newOffset >= (data.total || 0)) {
				if (sentinel) {
					sentinel.style.display = 'none';
				}
				if (lazyLoadObserver) {
					lazyLoadObserver.disconnect();
				}
			}
		} catch (e) {
			if (lazyLoadObserver) {
				lazyLoadObserver.disconnect();
			}
			if (sentinel) {
				sentinel.style.display = 'none';
			}
		} finally {
			isLoadingMore = false;
			if (sentinel) {
				sentinel.classList.remove('is-loading');
			}
		}
	};

	/**
	 * Disconnects any existing IntersectionObserver and, if a next page
	 * exists, creates a new one to trigger loadMore() when the sentinel
	 * scrolls into view.
	 *
	 * @param {boolean} hasNextPage - Whether there are more results to load.
	 */
	const resetLazyLoadObserver = (hasNextPage) => {
		if (lazyLoadObserver) {
			lazyLoadObserver.disconnect();
			lazyLoadObserver = null;
		}

		if (!sentinel) {
			return;
		}

		if (hasNextPage) {
			sentinel.style.display = '';
			lazyLoadObserver = new IntersectionObserver((entries) => {
				if (entries[ 0 ].isIntersecting && !isLoadingMore) {
					loadMore();
				}
			}, { rootMargin: '400px 0px' });
			lazyLoadObserver.observe(sentinel);
		} else {
			sentinel.style.display = 'none';
		}
	};

	const render = initFilterBar({
		bar, form, list, endpoint, defaults: DEFAULTS, clearDefaults: CLEAR_DEFAULTS,
		multi: MULTI, paramsFromForm, normaliseSort,
		toggleKeys: ['offers', 'tomorrow'],

		onBeforeOpenScope(key, { modal, panel }) {
			if (panel) {
				const pKey = panel.getAttribute('data-panel') || '';
				let activeAtOpen = false;

				if (pKey === 'price') {
					const max = form.querySelector('input[name="max"]')?.value ?? '';
					activeAtOpen = max !== '' && Number(max) < 100;
				} else if (pKey === 'time') {
					activeAtOpen = !!form.querySelector('input[name="time[]"]:checked');
				} else if (pKey === 'genre') {
					activeAtOpen = !!form.querySelector('input[name="genre[]"]:checked');
				} else if (pKey === 'date') {
					const f = form.querySelector('input[name="from"]')?.value || '';
					const t = form.querySelector('input[name="to"]')?.value || '';
					activeAtOpen = !!(f || t);
				}

				panel.setAttribute('data-active', activeAtOpen ? '1' : '0');
				snapshotPanel(panel);
				adjustPopoverPosition(panel);
			}

			if (key === 'price' && panel) {
				bindPriceControls(panel);
			}

			if (key === 'all' && modal) {
				bindPriceControls(modal);

				const formTimes = Array.from(form.querySelectorAll('input[name="time[]"]:checked')).map((i) => i.value);
				modal.querySelectorAll('[data-modal-time]').forEach((chk) => {
					chk.checked = formTimes.includes(chk.value);
				});

				const formGenres = Array.from(form.querySelectorAll('input[name="genre[]"]:checked')).map((i) => i.value);
				modal.querySelectorAll('[data-modal-genre]').forEach((chk) => {
					chk.checked = formGenres.includes(chk.value);
				});

				// Single-thumb max-only slider; min axis removed.
				const formMax = form.querySelector('input[name="max"]')?.value || '';
				const modalMax = modal.querySelector('[data-modal-price] input[name="max"]');
				if (modalMax) {
					modalMax.value = formMax;
				}

				const formOffers = form.querySelector('input[name="offers"]')?.value === '1';
				const modalOffers = modal.querySelector('[data-modal-toggle="offers"]');
				if (modalOffers) {
					modalOffers.classList.toggle('is-active', formOffers);
					modalOffers.setAttribute('aria-pressed', formOffers ? 'true' : 'false');
				}

				paintChoiceActives(modal);
			}
		},

		onAfterRender(data, params) {
			filterTracker.track(params);
			paintChoiceActives(bar);
			setAllTriggersFromForm();
			updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI, toggleKeys: ['offers', 'tomorrow'] });
			updateClearChip(bar, params, CLEAR_DEFAULTS);
			initShowCardTabs();

			if (!sentinel) {
				return;
			}

			if (data && typeof data === 'object') {
				const total = parseInt(data.total || '0', 10);
				const offset = parseInt(data.offset || '0', 10);
				const limit = parseInt(data.limit || '24', 10);

				list.dataset.total = total;
				list.dataset.offset = offset + limit;
				list.dataset.limit = limit;

				resetLazyLoadObserver((offset + limit) < total);
			} else {
				const total = parseInt(list.dataset.total || '0', 10);
				const offset = parseInt(list.dataset.offset || '0', 10);

				resetLazyLoadObserver(total > 0 ? offset < total : false);
			}
		}
	});

	/*
	 * Mount the show search typeahead after the filter bar is initialised,
	 * now that render is available. The showSearch variable declared above
	 * is assigned here so that paramsFromForm can use it on subsequent calls.
	 */
	showSearch = mountShowSearch(bar, form, render);

	// --- Event: inline chip toggles (Today, Tomorrow, Access, Offers) -------

	bar.addEventListener('click', (e) => {
		const chip = e.target.closest('[data-filter-toggle]');
		if (!chip) {
			return;
		}

		const key = chip.getAttribute('data-filter-toggle');

		if (key === 'online_today' || key === 'online_tomorrow') {
			toggleDayChip(key);
			bar.querySelectorAll('[data-panel="date"]').forEach((p) => p.setAttribute('hidden', ''));
			setAllTriggersFromForm();
			return render();
		}

		if (key === 'access') {
			const inp = form.querySelector('input[name="access"]');
			if (inp) {
				inp.value = inp.value === '1' ? '' : '1';
			}
			chip.classList.toggle('is-active');
			setAllTriggersFromForm();
			return render();
		}

		if (key === 'offers') {
			const inp = form.querySelector('input[name="offers"]');
			if (inp) {
				inp.value = inp.value === '1' ? '' : '1';
			}
			chip.classList.toggle('is-active');
			chip.setAttribute('aria-pressed', inp?.value === '1' ? 'true' : 'false');
			setAllTriggersFromForm();
			return render();
		}
	});

	// Update the date trigger chip whenever the date range inputs change.
	form.querySelectorAll('input[name="from"], input[name="to"]').forEach((i) => {
		i.addEventListener('input', updateDateChipActive);
		i.addEventListener('change', updateDateChipActive);
	});

	// --- Event: popover panel Apply / Cancel buttons ------------------------

	bar.addEventListener('click', (e) => {
		const panel = e.target.closest('.popover__panel');
		if (!panel) {
			return;
		}

		const applyBtn = e.target.closest('[data-submit]');
		const clearBtn = e.target.closest('[data-cancel]');
		if (!applyBtn && !clearBtn) {
			return;
		}

		const key = panel.getAttribute('data-panel') || '';

		if (clearBtn) {
			/*
			 * If the panel had no active selections when it was opened, Cancel
			 * simply restores the snapshot and hides the panel without a re-render.
			 */
			const wasActiveAtOpen = panel.getAttribute('data-active') === '1';
			if (!wasActiveAtOpen) {
				restorePanel(panel);
				panel.setAttribute('hidden', '');
				return;
			}

			if (key === 'price') {
				panel.querySelectorAll('input[name="max"]').forEach((i) => (i.value = ''));
				panel.querySelector('[data-price-wrap]')?.resetPriceUI?.();

				let inlineMax = form.querySelector('input[name="max"]');
				if (!inlineMax) {
					inlineMax = document.createElement('input');
					inlineMax.type = 'hidden';
					inlineMax.name = 'max';
					form.appendChild(inlineMax);
				}
				inlineMax.value = '';
			} else if (key === 'time') {
				panel.querySelectorAll('input[name="time[]"]').forEach((i) => (i.checked = false));
			} else if (key === 'genre') {
				panel.querySelectorAll('input[name="genre[]"]').forEach((i) => (i.checked = false));
			} else if (key === 'date') {
				const inFrom = form.querySelector('input[name="from"]') || (() => {
					const i = document.createElement('input');
					i.type = 'hidden';
					i.name = 'from';
					form.appendChild(i);
					return i;
				})();
				const inTo = form.querySelector('input[name="to"]') || (() => {
					const i = document.createElement('input');
					i.type = 'hidden';
					i.name = 'to';
					form.appendChild(i);
					return i;
				})();
				inFrom.value = '';
				inTo.value = '';
			}

			setAllTriggersFromForm();
			paintChoiceActives(document);
			updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI, toggleKeys: ['offers', 'tomorrow'] });
			updateClearChip(bar, paramsFromForm(), CLEAR_DEFAULTS);
			panel.setAttribute('hidden', '');
			render();
			return;
		}

		if (applyBtn) {
			if (key === 'price') {
				const wrap = panel.querySelector('[data-price-wrap]');
				if (wrap) {
					const srcMax = wrap.querySelector('input[name="max"]');

					let inlineMax = form.querySelector('input[name="max"]');
					if (!inlineMax) {
						inlineMax = document.createElement('input');
						inlineMax.type = 'hidden';
						inlineMax.name = 'max';
						form.appendChild(inlineMax);
					}

					inlineMax.value = String(srcMax?.value ?? '');
				}
			}

			setAllTriggersFromForm();
			paintChoiceActives(document);
			updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI, toggleKeys: ['offers', 'tomorrow'] });
			updateClearChip(bar, paramsFromForm(), CLEAR_DEFAULTS);
			panel.setAttribute('hidden', '');
			render();
		}
	});

	// --- Event: All Filters modal Apply / Cancel ----------------------------

	bar.addEventListener('click', (e) => {
		const modal = e.target.closest('dialog[data-modal="all"]');
		if (!modal) {
			return;
		}

		const applyBtn = e.target.closest('[data-submit]');
		const cancelBtn = e.target.closest('[data-cancel]');

		if (applyBtn) {
			e.preventDefault();

			// If a data-modal-goto button is active, navigate immediately.
			// No filter state is written back to the form in this case.
			const gotoUrl = resolveModalGoto(modal);
			if (gotoUrl) {
				modal.close();
				window.location.href = gotoUrl;
				return;
			}

			const modalTimes = Array.from(modal.querySelectorAll('[data-modal-time]:checked')).map((i) => i.value);
			form.querySelectorAll('input[name="time[]"]').forEach((i) => {
				i.checked = modalTimes.includes(i.value);
			});

			const modalGenres = Array.from(modal.querySelectorAll('[data-modal-genre]:checked')).map((i) => i.value);
			form.querySelectorAll('input[name="genre[]"]').forEach((i) => {
				i.checked = modalGenres.includes(i.value);
			});

			const modalPriceWrap = modal.querySelector('[data-modal-price]');
			if (modalPriceWrap) {
				// Single-thumb max-only slider; min axis removed.
				const modalMax = modalPriceWrap.querySelector('input[name="max"]');

				let formMax = form.querySelector('input[name="max"]');
				if (!formMax) {
					formMax = document.createElement('input');
					formMax.type = 'hidden';
					formMax.name = 'max';
					form.appendChild(formMax);
				}

				formMax.value = modalMax?.value || '';
			}

			modal.close();
			setAllTriggersFromForm();
			paintChoiceActives(bar);
			updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI, toggleKeys: ['offers', 'tomorrow'] });
			updateClearChip(bar, paramsFromForm(), CLEAR_DEFAULTS);
			render();
			return;
		}

		if (cancelBtn) {
			e.preventDefault();
			modal.close();
		}
	});

	// --- Event: modal toggle buttons (e.g. Offers inside All Filters) -------

	bar.addEventListener('click', (e) => {
		const modalToggle = e.target.closest('[data-modal-toggle]');
		if (!modalToggle) {
			return;
		}

		const key = modalToggle.getAttribute('data-modal-toggle');

		if (key === 'offers') {
			const inp = form.querySelector('input[name="offers"]');
			if (inp) {
				const newVal = inp.value === '1' ? '' : '1';
				inp.value = newVal;
				modalToggle.classList.toggle('is-active', newVal === '1');
				modalToggle.setAttribute('aria-pressed', newVal === '1' ? 'true' : 'false');

				const modalEl = modalToggle.closest('dialog[data-modal="all"]');
				if (modalEl) {
					markScopeDirty(modalEl, true);
				}
			}
		}
	});

	// --- Search input with debounce -----------------------------------------

	const qInput = form.querySelector('input[name="q"]');
	if (qInput) {
		// Skip the network call for partial queries (1–2 chars): the REST
		// endpoint would strip `q` anyway. Empty (length 0) must still render
		// so clearing the box restores the full list; 3+ chars renders with
		// `q` included.
		const shouldRender = () => {
			const v = (qInput.value || '').trim();
			return v.length === 0 || has3Chars(v);
		};
		let t;
		qInput.addEventListener('input', () => {
			clearTimeout(t);
			t = setTimeout(() => {
				if (!shouldRender()) {
					return;
				}
				render();
			}, 250);
		});
		qInput.addEventListener('keydown', (e) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				if (!shouldRender()) {
					return;
				}
				render();
			}
		});
	}

	// --- Hydrate toggle chip states from the current URL params -------------

	const hydrateFlagsFromURL = () => {
		const url = new URL(window.location.href);
		const coerce = (v) => {
			const s = String(v || '').trim();
			return s === '1' || /^true|yes|on$/i.test(s);
		};

		const apply = (name, on) => {
			let input = form.querySelector(`input[name="${ name }"]`);
			if (!input) {
				input = document.createElement('input');
				input.type = 'hidden';
				input.name = name;
				form.appendChild(input);
			}
			input.value = on ? '1' : '';
		};

		const hasToday = url.searchParams.has('today');
		const hasTomorrow = url.searchParams.has('tomorrow');

		let t, tm;

		if (!hasToday && !hasTomorrow) {
			// No day param in URL: default to Today only.
			t = true;
			tm = false;
		} else {
			t = coerce(url.searchParams.get('today'));
			tm = coerce(url.searchParams.get('tomorrow'));

			// Prevent both being off simultaneously.
			if (!t && !tm) {
				t = true;
			}
		}

		const ac = coerce(url.searchParams.get('access'));
		const off = coerce(url.searchParams.get('offers'));

		apply('today', t);
		apply('tomorrow', tm);
		apply('access', ac);
		apply('offers', off);

		setHeaderChip('[data-filter-toggle="online_today"]', t);
		setHeaderChip('[data-filter-toggle="online_tomorrow"]', tm);
		setHeaderChip('[data-filter-toggle="access"]', ac);
		setHeaderChip('[data-filter-toggle="offers"]', off);

		if (t || tm) {
			const f = form.querySelector('input[name="from"]');
			const to = form.querySelector('input[name="to"]');
			if (f) {
				f.value = '';
			}
			if (to) {
				to.value = '';
			}
			updateDateChipActive();
		}
	};

	window.addEventListener('popstate', () => {
		hydrateFlagsFromURL();
		paintChoiceActives(document);
		setAllTriggersFromForm();
		updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI, toggleKeys: ['offers', 'tomorrow'] });
		updateClearChip(bar, paramsFromForm(), CLEAR_DEFAULTS);
		render();
	});

	// --- Initial paint ------------------------------------------------------

	hydrateFlagsFromURL();
	paintChoiceActives(document);
	setAllTriggersFromForm();
	updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI, toggleKeys: ['offers', 'tomorrow'] });
	updateClearChip(bar, paramsFromForm(), CLEAR_DEFAULTS);

	// Baseline after URL flag hydration and show-search mount, so the default
	// "today" flag and any pre-selected shows are not reported as new applies.
	filterTracker.init(paramsFromForm());

	/*
	 * Skip the initial fetch if the page was server-side rendered with results
	 * and no filter params are present in the URL. In that case, simply set up
	 * the lazy-load observer against the existing SSR content.
	 */
	const hasSSR = list?.dataset?.ssrInitial === '1' && (list.firstElementChild || (list.textContent || '').trim().length > 0);
	const u = new URL(location.href);
	const hasParams = (u.search || '').length > 1;

	if (!hasSSR || hasParams) {
		render();
	} else {
		const initialTotal = parseInt(list.dataset.total || '0', 10);
		const initialOffset = parseInt(list.dataset.offset || '0', 10);
		resetLazyLoadObserver(initialOffset < initialTotal);
	}
}
